<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { visibleNavigationSections } from '../../access/navigation.js'
import { useI18n } from '../../i18n'
import SidebarSection from './SidebarSection.vue'

const props = defineProps({
  user: { type: Object, required: true },
  area: { type: String, default: 'naftapos' },
})
const emit = defineEmits(['close', 'navigate'])
const panel = ref(null)
const sections = computed(() => visibleNavigationSections(props.user, props.area))
const { t } = useI18n()
let previousFocus = null
let mobileOverlay = false

function focusableItems() {
  if (!panel.value) return []
  return [...panel.value.querySelectorAll('button,[href],[tabindex]:not([tabindex="-1"])')]
    .filter((item) => !item.disabled)
}

function keydown(event) {
  if (!mobileOverlay) return
  if (event.key === 'Escape') {
    event.preventDefault()
    emit('close')
    return
  }
  if (event.key !== 'Tab') return
  const items = focusableItems()
  if (!items.length) return
  const first = items[0]
  const last = items[items.length - 1]
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

onMounted(async () => {
  mobileOverlay = window.innerWidth < 1024
  if (!mobileOverlay) return
  previousFocus = document.activeElement
  document.body.classList.add('drawer-open')
  await nextTick()
  panel.value?.querySelector('.sidebar-close-button')?.focus()
})

onBeforeUnmount(() => {
  if (!mobileOverlay) return
  document.body.classList.remove('drawer-open')
  previousFocus?.focus?.()
})
</script>

<template>
  <aside ref="panel" id="application-navigation" class="app-sidebar" @keydown="keydown">
    <button class="sidebar-close-button" type="button" :aria-label="t('menuClose')" @click="emit('close')">×</button>
    <nav :aria-label="t('navigation')">
      <SidebarSection v-for="(section, index) in sections" :key="section.labelKey"
        :section="section" :divided="index > 0" @navigate="emit('navigate')" />
    </nav>
  </aside>
</template>
